/* eslint-disable no-param-reassign */
import moment from 'moment';
import uuid from 'react-uuid';
import { MeasurementDate } from 'api';
import { FIRST_ROW_TITLE, FISCAL_YEAR, FORMAT_YEAR_TITLE } from 'common/constants/financials';
import { FinancialStatementPeriod } from 'common/types/financials';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { template as balanceSheetTemplate } from 'pages/Financials/balance-sheet/data';
import { template as performanceMetricsTemplate } from 'pages/Financials/performance-metrics/data';
import { ISO_DATE_FORMAT } from 'utilities/dateFormats/constants';
import { getReferenceIndex } from './auxiliaryFunctions';
import getFinancialsData from './getFinancialsData';
import { BalanceSheetColumn, FinancialPeriodColumn } from './types';
import incomeStatementTemplate from '../income-statement/data/template';
import { KpiReverseParserType } from '../kpi/config/types';

const separateColumnsByIndex = (columns: FinancialPeriodColumn[], referenceIndex: number, isPrevYear: boolean) => {
  const initialColumns = isPrevYear ? [] : [...columns].slice(0, referenceIndex + 1);
  const lastColumns = isPrevYear ? columns : [...columns].slice(referenceIndex + 1);
  return { initialColumns, lastColumns };
};

const addFinancialYear = async ({
  tableData,
  financialStatement,
  periods,
  scrollToEndOfTables,
  defaultLimitYear,
  additionalHistoricalYears,
  setAdditionalHistoricalYears,
  isPrevYear = false,
  measurementDate,
}: {
  tableData: {
    incomeStatement: SpreadsheetConfig;
    balanceSheet: SpreadsheetConfig;
    KPI: KpiReverseParserType;
    performanceMetrics: SpreadsheetConfig;
    evaluateSpreadsheets: (spreadsheets: SpreadsheetConfig[]) => Promise<void>;
  };
  financialStatement: FinancialStatementPeriod;
  periods: FinancialPeriodColumn[];
  scrollToEndOfTables: (isPrevYear: boolean) => void;
  defaultLimitYear: number;
  additionalHistoricalYears: FinancialStatementPeriod[];
  setAdditionalHistoricalYears: (additionalHistoricalYears: FinancialStatementPeriod[]) => void;
  isPrevYear: boolean;
  measurementDate: MeasurementDate;
}) => {
  const newColumns: FinancialPeriodColumn[] = [];
  const { incomeStatement, balanceSheet, KPI } = tableData;

  const { financial_statement_periods: statementPeriods } = getFinancialsData(
    incomeStatement,
    balanceSheet,
    KPI,
    financialStatement,
    periods,
    measurementDate
  );
  /*
  1. get valid periods (is_deleted == false)
  2. create Map from financials statement periods
  3. Get updated periods with mapping
  4. Determine insertion position
  5. create updated array of periods
  */
  // 1.
  const validPeriods = periods.filter(({ is_deleted }) => !is_deleted);

  // 2.
  const fspMap = new Map();

  statementPeriods.forEach(period => {
    const key = `${period.period_type}-${period.statement_date}`;
    fspMap.set(key, period);
  });

  // 3.
  const updatedPeriods = validPeriods.map(period => ({
    ...period,
    ...fspMap.get(`${period.period_type}-${period.statement_date}`),
  }));

  // 4.
  // separate the LTM, NTM periods and sort the rest. Then combine again
  const regularPeriods = updatedPeriods.filter(({ period_type }) => period_type === FISCAL_YEAR);

  const specialPeriods = updatedPeriods.filter(({ period_type }) => period_type !== FISCAL_YEAR);

  regularPeriods.sort((a, b) => {
    if (a.period_type === b.period_type) {
      return moment(a.statement_date).diff(moment(b.statement_date));
    }
    return 0;
  });

  const updatedSortedPeriods = [...regularPeriods, ...specialPeriods];

  const referenceIndex = getReferenceIndex(updatedSortedPeriods, isPrevYear)!;
  const { initialColumns, lastColumns } = separateColumnsByIndex(updatedSortedPeriods, referenceIndex, isPrevYear);

  const referencePeriod = validPeriods[referenceIndex];

  const newYear = isPrevYear
    ? moment(referencePeriod?.statement_date).subtract(1, 'years')
    : moment(referencePeriod?.statement_date).add(1, 'years');

  const newYearId: string = uuid();

  const year = newYear.year();
  const isAdditionalHistoricalYear = year <= defaultLimitYear;

  if (isAdditionalHistoricalYear) {
    const tmpAdditionalHistoricalYears = additionalHistoricalYears;
    tmpAdditionalHistoricalYears.unshift({ id: newYearId, year } as unknown as FinancialStatementPeriod);
    setAdditionalHistoricalYears(tmpAdditionalHistoricalYears);
  }
  // at this point we should determine if this is truly a new period or if we got to bring back a temporarily deleted periods
  // with blank data to avoid an integrity error when trying to save
  const temporarilyDeletedYear = periods.find(p => p.period_type === FISCAL_YEAR && p.year === year.toString());
  if (temporarilyDeletedYear) {
    temporarilyDeletedYear.is_deleted = false;
    newColumns.push(temporarilyDeletedYear);
  } else {
    const year = moment(newYear).format('YYYY');
    const fiscalYearLabel = FORMAT_YEAR_TITLE(year);
    const name = isPrevYear ? fiscalYearLabel : FIRST_ROW_TITLE;
    newColumns.push({
      statement_date: moment(newYear).format(ISO_DATE_FORMAT),
      period_type: FISCAL_YEAR,
      is_projection: !isPrevYear,
      name,
      id: newYearId,
      title: isPrevYear ? fiscalYearLabel : FIRST_ROW_TITLE,
      subtitle: isPrevYear ? null : fiscalYearLabel,   
      isParent: false,
      isPrevYear,
      isProjectedYear: !isPrevYear,
      isAdditionalHistoricalYear,
      showAdditionalHistoricalYear: true,
      isVisibleColumn: Boolean(isAdditionalHistoricalYear),
      income_statement: { name },
      balance_sheet: { name },
    });
  }

  const incomeStatementColumns = newColumns.map(newColumn => {
    if (newColumn.id && temporarilyDeletedYear) {
      return {
        ...newColumn,
        income_statement: {
          ...newColumn.income_statement,
          ...incomeStatementTemplate,
          name: newColumn.income_statement?.name,
        },
        financial_statement_period: newColumn.id,
      };
    }
    const data = newColumn.income_statement ?? {};
    return {
      ...incomeStatementTemplate,
      ...data,
      ...newColumn,
    };
  });

  const performanceMetricsColumns = newColumns.map(newColumn => ({
    ...performanceMetricsTemplate,
    ...newColumn,
  }));

  const balanceSheetColumns = newColumns.map(newColumn => {
    if (newColumn.id && temporarilyDeletedYear) {
      return {
        ...newColumn,
        balance_sheet: {
          ...newColumn.balance_sheet,
          ...balanceSheetTemplate,
          name: newColumn.balance_sheet?.name,
        },
        financial_statement_period: newColumn.id,
      };
    }
    const data = newColumn.balance_sheet ?? {};
    return {
      ...balanceSheetTemplate,
      ...data,
      ...newColumn,
    };
  });

  const kpiColumns = newColumns.map(newColumn => ({ ...newColumn, kpi_dataset: [] }));

  /* tableData.<financialSection>.columns doesn't have the temp data
  -> get from updatedPeriods the income_statement prop that has the temp data */
  tableData.incomeStatement.columns = updatedSortedPeriods.map((col, idx) => {
    if ('id' in validPeriods[idx]) {
      return { ...col, id: validPeriods[idx].id };
    }
    return col;
  });

  tableData.balanceSheet.columns = tableData.balanceSheet.columns.map((col: BalanceSheetColumn, idx: number) => {
    if ('id' in col) {
      return { ...updatedSortedPeriods[idx], id: col.id };
    }
    return updatedSortedPeriods[idx];
  });

  // Find the LTM column position in the current table columns
  const findLTMColumnIndex = (columns: any[]) => columns.findIndex(column => column.isLTM);

  const ltmIndex = findLTMColumnIndex(tableData.incomeStatement.columns);
  // For new projection years, insert before LTM. For historical years, insert at beginning
  let insertionIndexIncomeStatement = 0;
  if (!isPrevYear) {
    insertionIndexIncomeStatement = ltmIndex > -1 ? ltmIndex : tableData.incomeStatement.columns.length - 2;
  }

  await tableData.incomeStatement.addColumns(
    insertionIndexIncomeStatement,
    incomeStatementColumns
  );

  const ltmIndexPerformance = findLTMColumnIndex(tableData.performanceMetrics.columns);
  let insertionIndexPerformanceMetrics = 0;
  if (!isPrevYear) {
    insertionIndexPerformanceMetrics = ltmIndexPerformance > -1 ? ltmIndexPerformance : tableData.performanceMetrics.columns.length - 2;
  }

  await tableData.performanceMetrics.addColumns(
    insertionIndexPerformanceMetrics,
    performanceMetricsColumns
  );

  const ltmIndexBalance = findLTMColumnIndex(tableData.balanceSheet.columns);
  let insertionIndexBalanceSheet = 0;
  if (!isPrevYear) {
    insertionIndexBalanceSheet = ltmIndexBalance > -1 ? ltmIndexBalance : tableData.balanceSheet.columns.length - 1;
  }

  await tableData.balanceSheet.addColumns(
    insertionIndexBalanceSheet,
    balanceSheetColumns
  );

  await tableData.KPI.addColumns(
    isPrevYear ? 0 : tableData.KPI.columns.length, // don't have LTM and NTM periods
    kpiColumns
  );

  await tableData.evaluateSpreadsheets([
    tableData.incomeStatement,
    tableData.performanceMetrics,
    tableData.balanceSheet,
    tableData.KPI,
  ]);

  periods.splice(0, periods.length, ...initialColumns, ...newColumns, ...lastColumns);

  scrollToEndOfTables(isPrevYear);
};

export default addFinancialYear;
